/**
 * @file RollbackDialog.tsx
 * @description Окно «Вернуть вид сайта от …?» (phase19.16 Р1; SB52, SB53,
 * SB59, SB72; uiux-design-r1 §6; контракт §6): что вернётся и что не
 * изменится, предупреждения (потеря неопубликованных правок, пункты меню на
 * удалённые цели, удалённые из медиатеки картинки) и проверки публикации.
 * «Вернуть эту версию» публикует её сразу (SB72).
 */

import { SITE_VIEW_PART_LABELS } from "@chaiberi/shared";
import { useEffect, useState } from "react";

import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../ui/dialog";
import { useNotify } from "../../ui/NotifyProvider";
import { PublishChecksList } from "../dialogs/PublishChecksList";
import { useSiteView } from "../site-view-context";
import { siteView } from "../../../lib/api-site-view";
import { parsePublishBlocked, parsePublishForbidden, siteViewErrorText } from "../../../lib/site-view/errors";
import { historyWhen } from "../../../lib/site-view/history";
import type { SiteHistoryItem, SiteRollbackPreview } from "../../../lib/site-view/types";

interface Props {
  item: SiteHistoryItem | null;
  onClose: () => void;
  onDone: () => void;
}

export function RollbackDialog({ item, onClose, onDone }: Props) {
  const view = useSiteView();
  const notify = useNotify();
  const [preview, setPreview] = useState<SiteRollbackPreview | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setPreview(null);
    setProblem(null);
    if (!item) return;
    siteView
      .rollbackPreview(item.version)
      .then(setPreview)
      .catch((err: unknown) => setProblem(siteViewErrorText(err, "Не удалось проверить версию. Повторите позже.")));
  }, [item]);

  const blocking = preview?.checks.some((c) => c.level === "error") ?? false;

  async function onConfirm() {
    if (!item) return;
    setBusy(true);
    try {
      await siteView.rollback(item.version);
      notify.success("Версия возвращена и опубликована. Покупатели увидят её в течение минуты.");
      await view.reload();
      onDone();
    } catch (err) {
      const checks = parsePublishBlocked(err);
      if (checks && preview) setPreview({ ...preview, checks });
      else if (parsePublishForbidden(err)) setProblem("Вернуть версию может владелец магазина: она затрагивает оформление и меню.");
      else notify.error(siteViewErrorText(err, "Не удалось вернуть версию — сайт остался прежним."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={item !== null} onOpenChange={(open) => (!open ? onClose() : null)}>
      <DialogContent fullScreen className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Вернуть вид сайта от {item ? historyWhen(item) : ""}?</DialogTitle>
          <DialogDescription>
            Это новая публикация: покупатели увидят этот вид в течение минуты. Вернутся цвета, шрифты, шапка, подвал, меню,
            настройки сайта. Товары, цены, заказы, статьи, отзывы, реквизиты, правовые документы и SEO («Сайт → SEO») не изменятся.
          </DialogDescription>
        </DialogHeader>
        {problem ? <p className="type-body text-destructive">{problem}</p> : null}
        {!preview && !problem ? <p className="type-body text-on-surface-variant">Проверяем версию…</p> : null}
        {preview ? (
          <ul className="type-body flex flex-col gap-1 text-on-surface">
            {preview.lostDraftParts.length > 0 ? (
              <li className="text-warning">
                ! Неопубликованные изменения ({preview.lostDraftParts.map((p) => `${SITE_VIEW_PART_LABELS[p.part]} ${p.count}`).join(", ")}) будут удалены.
              </li>
            ) : null}
            {preview.orphanedMenuItems.map((m) => (
              <li key={`${m.location}:${m.label}`} className="text-warning">
                ! Пункт «{m.label}» ведёт на удалённую страницу — на сайте он будет скрыт.
              </li>
            ))}
            {preview.missingMedia.map((m) => (
              <li key={m.field} className="text-warning">
                ! {m.label} из этой версии удалён из медиатеки — на сайте его не будет.
              </li>
            ))}
          </ul>
        ) : null}
        {preview && preview.checks.length > 0 ? <PublishChecksList checks={preview.checks} /> : null}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="brand" disabled={!preview || blocking || !!problem} loading={busy} onClick={() => void onConfirm()}>
            Вернуть эту версию
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
